import { twMerge } from "tailwind-merge";

import type { TableRow } from "../../../types";
import {
  CHECKBOX_COLUMN_ID,
  NAME_COLUMN_ID,
  PERIOD_COLUMN_ID,
  RATE_COLUMN_ID,
  TOTAL_COLUMN_ID,
  ZONE_COLUMN_ID,
} from "../../dataFunctions/headers";
import { ReportingTableCheckbox } from "../checkbox";
import {
  type StickyPosition,
  formatNumber,
  formatRate,
  getCellPaddingClass,
  getRateColorClass,
  getStickyStyle,
} from "../utils";
import { ReportingTableNameCell } from "./nameCell";

type Props = {
  columnId: string;
  row: TableRow;
  isIndividualTab: boolean;
  stickyPosition?: StickyPosition;
  // Sticky cells need an opaque background to cover the cells scrolled under them.
  background: string;
  isSelected: boolean;
  onToggleRow: (key: string) => void;
};

const getBodyCellContent = ({
  columnId,
  row,
  isSelected,
  onToggleRow,
}: Props): { className?: string; content?: React.ReactNode } => {
  switch (columnId) {
    case CHECKBOX_COLUMN_ID:
      return {
        content: (
          <ReportingTableCheckbox
            checked={isSelected}
            onToggle={() => onToggleRow(row.key)}
          />
        ),
      };
    case NAME_COLUMN_ID:
      return {
        className: "overflow-hidden text-left text-ellipsis whitespace-nowrap",
        content: <ReportingTableNameCell name={row.name} link={row.link} />,
      };
    case PERIOD_COLUMN_ID:
      return {
        className: "text-left whitespace-nowrap",
        content: (
          <>
            <span className="text-[13.5px] font-semibold">{row.name}</span>
            {row.subLabel && (
              <span className="ml-[8px] text-[12px] text-[#9a958f]">
                {row.subLabel}
              </span>
            )}
            {row.dateRange && (
              <div className="text-[11.5px] text-[#9a958f] tabular-nums">
                {row.dateRange}
              </div>
            )}
          </>
        ),
      };
    case ZONE_COLUMN_ID:
      return {
        className: "text-left text-[13px] whitespace-nowrap text-[#4a4747]",
        content: row.zone,
      };
    case RATE_COLUMN_ID:
      return {
        className: twMerge(
          "text-[13px] font-semibold tabular-nums",
          getRateColorClass(row.rate),
        ),
        content: formatRate(row.rate),
      };
    case TOTAL_COLUMN_ID:
      return {
        className:
          "border-l-2 border-l-[#E4DDDD] text-[13px] font-bold tabular-nums",
        content: formatNumber(row.total),
      };
    default: {
      const value = row.cells[columnId] ?? 0;
      return {
        className:
          "border-l border-l-[#F4EFEF] text-[12.5px] text-[#1D1C1C] tabular-nums",
        content: value !== 0 ? formatNumber(value) : "",
      };
    }
  }
};

export const ReportingTableBodyCell: React.FC<Props> = (props) => {
  const { columnId, isIndividualTab, stickyPosition, background } = props;
  const { className, content } = getBodyCellContent(props);

  return (
    <td
      style={stickyPosition && getStickyStyle(stickyPosition, { background })}
      className={twMerge(
        "border-b border-b-[#F1EAEA] py-[10px] text-right",
        getCellPaddingClass(columnId, isIndividualTab),
        stickyPosition && "z-3",
        className,
      )}
    >
      {content}
    </td>
  );
};
